import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import Seo from "@/components/Seo";
import { CheckRow, Label, PageHero, SectionHead } from "@/components/Shared";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import ContactForm from "@/components/ContactForm";
import { Stadtmusikanten, BremenSkyline } from "@/components/graphics/Marks";
import Snowfall from "@/components/Snowfall";
import { BRAND, IMG } from "@/lib/site";

const crumbs = [
  { label: "Startseite", to: "/" },
  { label: "Kontakt", to: "/kontakt" },
];

const Kontakt = () => (
  <main data-testid="page-kontakt">
    <Seo
      title="Kontakt | Winterdienst Bremen anfragen | Bremer Schneemann"
      description="Winterdienst in Bremen anfragen: Telefon +49 421 16152637, WhatsApp oder E-Mail an info@bremer-schneemann.de. Bremer Schneemann, Flughafenallee 26, 28199 Bremen."
      path="/kontakt"
      breadcrumbs={crumbs}
      image={IMG.heroStreet}
    />
    <PageHero
      label="/ Kontakt"
      titleLines=    {["Winterdienst in", "Bremen anfragen."]}
      intro="Füllen Sie das kurze Anfrageformular aus und senden Sie es per WhatsApp oder E-Mail – oder rufen Sie direkt an. Das Angebot erstellen wir immer objektbezogen."
      image={IMG.heroStreet}
      breadcrumbs={crumbs}
    />


    <section className="bg-white py-16 lg:py-24">
      <div className="shell grid gap-12 lg:grid-cols-[1.08fr_0.92fr] lg:gap-16">
        <ContactForm />

        <div>
          <SectionHead
            label="/ So erreichen Sie uns"
            title="Direkt bei Bremer Schneemann – ohne Zentrale und ohne Warteschleife."
            text="Lieber telefonisch? Kein Problem – alle Wege führen zur gleichen Person."
          />

          <Stagger className="mt-10 grid gap-3">
            <StaggerItem>
              <a
                href={BRAND.phoneHref}
                data-testid="kontakt-phone"
                className="group flex items-center justify-between gap-5 rounded-2xl border border-[#DCE5EF] bg-white p-6 transition-all duration-500 hover:-translate-y-1 hover:border-[#0757B8] hover:shadow-[0_30px_70px_-52px_rgba(6,43,82,0.6)]"
              >
                <span className="flex items-center gap-4">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#EEF6FF] text-[#0757B8]">
                    <Phone className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="font-mono-label block text-[#66788A]">Telefon</span>
                    <span className="mt-1 block text-[17px] font-bold tracking-tight">{BRAND.phone}</span>
                  </span>
                </span>
                <ArrowUpRight className="h-5 w-5 text-[#FF6B00] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </StaggerItem>

            <StaggerItem>
              <a
                href={BRAND.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="kontakt-whatsapp"
                className="group flex items-center justify-between gap-5 rounded-2xl bg-[#25D366] p-6 text-white transition-transform duration-500 hover:-translate-y-1"
              >
                <span className="flex items-center gap-4">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/20">
                    <MessageCircle className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="font-mono-label block text-white/80">WhatsApp</span>
                    <span className="mt-1 block text-[17px] font-bold tracking-tight">{BRAND.whatsappNumber}</span>
                  </span>
                </span>
                <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </StaggerItem>

            <StaggerItem>
              <a
                href={BRAND.emailHref}
                data-testid="kontakt-email"
                className="group flex items-center justify-between gap-5 rounded-2xl border border-[#DCE5EF] bg-white p-6 transition-all duration-500 hover:-translate-y-1 hover:border-[#0757B8] hover:shadow-[0_30px_70px_-52px_rgba(6,43,82,0.6)]"
              >
                <span className="flex min-w-0 items-center gap-4">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#EEF6FF] text-[#0757B8]">
                    <Mail className="h-5 w-5" />
                  </span>
                  <span className="min-w-0">
                    <span className="font-mono-label block text-[#66788A]">E-Mail</span>
                    <span className="mt-1 block break-all text-[15px] font-bold tracking-tight">{BRAND.email}</span>
                  </span>
                </span>
                <ArrowUpRight className="h-5 w-5 shrink-0 text-[#FF6B00]" />
              </a>
            </StaggerItem>
          </Stagger>

          <Reveal delay={0.12}>
            <div className="mt-6 rounded-2xl border border-[#DCE5EF] bg-[#F5F8FC] p-6">
              <Label>/ Adresse</Label>
              <p className="mt-4 flex items-start gap-3 text-[14.5px] leading-relaxed text-[#10253A]">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#FF6B00]" />
                <span>
                  <strong className="font-semibold">Hauptbüro / Firmensitz</strong>
                  <br />
                  {BRAND.name}
                  <br />
                  {BRAND.street}
                  <br />
                  {BRAND.streetExtra}
                  <br />
                  {BRAND.zip} {BRAND.city}
                  <br />
                  <span className="mt-2 block text-[13px] text-[#66788A]">
                    <strong className="font-semibold text-[#10253A]">Fuhrpark:</strong>{" "}
                    {BRAND.fleet}
                  </span>
                </span>
              </p>
              <p className="mt-5 flex items-start gap-3 text-[14px] leading-relaxed text-[#66788A]">
                <Clock className="mt-0.5 h-4 w-4 shrink-0 text-[#0757B8]" />
                Während der Wintersaison halten wir Bereitschaft für betreute Objekte – Einsätze
                erfolgen nach vereinbarten Kriterien und Prioritäten.
              </p>
            </div>
          </Reveal>
        </div>

        <div>
          <Reveal>
            <div className="relative overflow-hidden rounded-[22px] bg-[#0757B8] p-7 text-white sm:p-9">
              <Snowfall count={16} />
              <BremenSkyline className="pointer-events-none absolute inset-x-0 bottom-0 h-16 w-full opacity-[0.16]" color="#FFFFFF" />
              <Stadtmusikanten className="pointer-events-none absolute -right-4 bottom-0 h-40 opacity-[0.12]" color="#FFFFFF" />
              <div className="relative z-10">
                <Label tone="white">/ Anfrage</Label>
                <h2 className="mt-3 text-[22px] font-bold leading-snug sm:text-[27px]" style={{ color: "#fff" }}>
                  Das brauchen wir für ein Angebot
                </h2>
                <ul className="mt-7 space-y-3.5">
                  <CheckRow light>Adresse des Objekts in Bremen</CheckRow>
                  <CheckRow light>Objektart – privat, Wohnanlage oder Gewerbe</CheckRow>
                  <CheckRow light>Welche Flächen: Gehweg, Einfahrt, Hof, Parkplatz, Treppen</CheckRow>
                  <CheckRow light>Ungefähre Flächengröße oder Länge des Gehwegs</CheckRow>
                  <CheckRow light>Saisonbetreuung oder Einzeleinsatz</CheckRow>
                  <CheckRow light>Besonderheiten: Gefälle, Kopfsteinpflaster, Zeitfenster</CheckRow>
                </ul>
                <p className="mt-7 border-t border-white/15 pt-6 text-[13.5px] leading-relaxed text-white/70">
                  Preise nennen wir immer objektbezogen – Angebot auf Anfrage. Feste Pauschalen
                  passen beim Winterdienst selten zur tatsächlichen Fläche.
                </p>
                <div className="mt-6 grid gap-2.5 sm:grid-cols-2">
                  <a href={BRAND.phoneHref} data-testid="kontakt-box-phone" className="btn h-12 bg-white text-[13.5px] text-[#062B52] hover:bg-[#EEF6FF]">
                    <Phone className="h-4 w-4" /> Jetzt anrufen
                  </a>
                  <a
                    href={BRAND.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="kontakt-box-whatsapp"
                    className="btn btn-wa h-12 text-[13.5px]"
                  >
                    <MessageCircle className="h-4 w-4" /> WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="mt-5 grid gap-2">
              {[
                { t: "Alle Leistungen ansehen", to: "/leistungen" },
                { t: "Winterdienst für Privatkunden", to: "/winterdienst-bremen-privat" },
                { t: "Winterdienst für Gewerbekunden", to: "/winterdienst-bremen-gewerbe-hausverwaltung" },
                { t: "Einsatzgebiet in Bremen", to: "/einsatzgebiet" },
                { t: "Häufige Fragen", to: "/faq" },
              ].map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`kontakt-link${l.to.replace(/\//g, "-")}`}
                  className="group flex items-center justify-between gap-3 rounded-xl border border-[#DCE5EF] bg-white px-5 py-4 text-[14px] font-medium text-[#10253A] transition-colors hover:border-[#0757B8] hover:text-[#0757B8]"
                >
                  {l.t}
                  <ArrowUpRight className="h-4 w-4 text-[#FF6B00] transition-transform group-hover:translate-x-0.5" />
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  </main>
);

export default Kontakt;
